import { useState, useEffect } from "react";
import SuccessToast from "../../components/toasts/success-toast";
import WarningToast from "../../components/toasts/warning-toast";

const faqs = [
  {
    q: "How do I create a new job opening?",
    a: "Go to Job Openings from the sidebar, click the Create button, and fill in the position name, hiring type, rounds and location.",
  },
  {
    q: "How do I invite a candidate to an interview?",
    a: "Open Send Invites, choose a published position, paste candidate emails or upload a CSV, then click Send Invite.",
  },
  {
    q: "Where can I see candidate scores?",
    a: "Round Scores shows scores per interview round. Score Dashboard gives you an aggregated view across all positions.",
  },
  {
    q: "How do I add a new interviewer?",
    a: "Open Position Details, scroll to Interviewers, click the + icon and fill in the interviewer's name, email and contact number.",
  },
  {
    q: "Can I customize question rounds?",
    a: "Yes — go to Question Library, choose a position and round, then add questions from scratch, the question bank, or a previous position template.",
  },
  {
    q: "How do I reset my password?",
    a: "Sign out, then click Forgot password on the sign-in page. You'll receive a reset link by email.",
  },
];

const HelpSupport = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [form, setForm] = useState({ subject: "", message: "" });

  useEffect(() => {
    document.title = "Help & Support - Ample Hire";
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      WarningToast("Please fill in both subject and message");
      return;
    }
    const tickets = JSON.parse(
      localStorage.getItem("ah_support_tickets") || "[]"
    );
    tickets.push({ ...form, createdAt: new Date().toISOString() });
    localStorage.setItem("ah_support_tickets", JSON.stringify(tickets));
    SuccessToast(
      "Your message has been recorded. Our team will reach out shortly."
    );
    setForm({ subject: "", message: "" });
  };

  return (
    <div className="p-6 bg-vf-bg min-h-full">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-vf-base">
          Help &amp; Support
        </h1>
        <p className="mt-1 text-sm text-vf-muted">
          Find answers to common questions or contact our support team.
        </p>
      </div>

      {/* FAQ */}
      <div className="card p-6 mb-6">
        <h2 className="text-base font-semibold text-vf-base mb-4">
          Frequently Asked Questions
        </h2>
        <div className="divide-y divide-vf-base/10">
          {faqs.map((f, i) => (
            <div key={i} className="py-3 first:pt-0 last:pb-0">
              <button
                className="w-full flex items-center justify-between text-left gap-3"
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
              >
                <span className="text-sm font-medium text-vf-base">
                  {f.q}
                </span>
                <span className="text-vf-muted text-lg shrink-0 leading-none">
                  {openIdx === i ? "−" : "+"}
                </span>
              </button>
              {openIdx === i && (
                <p className="mt-2 text-sm text-vf-muted leading-relaxed">
                  {f.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Support */}
      <div className="card p-6 mb-6">
        <h2 className="text-base font-semibold text-vf-base mb-4">
          Contact Support
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-vf-primary">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5H4.5a2.25 2.25 0 00-2.25 2.25m19.5 0l-9.75 6.75L2.25 6.75"
                />
              </svg>
            </span>
            <div>
              <div className="input-label">Email</div>
              <a
                href="mailto:support@amplehire.com"
                className="text-sm text-vf-accent hover:underline"
              >
                support@amplehire.com
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-vf-primary">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                />
              </svg>
            </span>
            <div>
              <div className="input-label">Phone</div>
              <div className="text-sm text-vf-base">+91 80 4567 8900</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-vf-primary">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </span>
            <div>
              <div className="input-label">Hours</div>
              <div className="text-sm text-vf-base">
                Mon – Fri, 9:00 – 18:00 IST
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit a Ticket */}
      <div className="card p-6">
        <h2 className="text-base font-semibold text-vf-base mb-4">
          Submit a Ticket
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="input-label mb-1.5 block">Subject</label>
            <input
              type="text"
              className="input w-full"
              placeholder="Briefly describe your issue"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
          </div>
          <div>
            <label className="input-label mb-1.5 block">Message</label>
            <textarea
              className="textarea w-full"
              rows={5}
              placeholder="Please provide as much detail as possible..."
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary btn-sm">
              Send Message
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HelpSupport;
